// Per-trigger row component for Settings → Notifications.
//
//   - <fieldset> + <legend> + Manrope description + an On/Off Seg.
//   - WS3 take-two consolidation: the bespoke 2-pill toggle was replaced by the
//     shared `Seg` primitive (radiogroup / aria-checked, card-thumb selection).
//   - One component, four call-sites (4 trigger names). The component switches
//     on the `trigger` prop to emit the per-trigger i18n key for legend +
//     description (e.g. `settings.notifications.opponent_moved.label`).
//   - Trigger-name → i18n-suffix mapping: the prop value is `tournament_cycle`
//     but the human-readable namespace uses `tournament` (singular). See
//     16-UI-SPEC.md §Copywriting Contract — the prop is the backend PushKind
//     value; the UI key is the singular noun. The map lives below in TRIGGER_KEY.

import type { JSX } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Seg } from '../components/Seg'

export type NotificationTrigger =
  | 'opponent_moved'
  | 'bot_substitution'
  | 'daily_streak'
  | 'tournament_cycle'

export interface NotificationTriggerToggleProps {
  trigger: NotificationTrigger
  selected: boolean
  onChange: (next: boolean) => void
}

/**
 * Map the prop-side trigger key (matches backend `PushKind`) onto the i18n
 * key fragment used in `settings.notifications.<key>.{label,desc}`. The map
 * is intentionally explicit so a future trigger addition fails tsc loudly.
 */
const TRIGGER_KEY: Record<NotificationTrigger, string> = {
  opponent_moved: 'opponent_moved',
  bot_substitution: 'bot_substitution',
  daily_streak: 'daily_streak',
  // The component prop value is `tournament_cycle` (backend PushKind), but
  // the human-readable i18n namespace is `tournament` (singular). See
  // 16-UI-SPEC.md §Copywriting Contract.
  tournament_cycle: 'tournament',
}

type OnOff = 'on' | 'off'

export function NotificationTriggerToggle({
  trigger,
  selected,
  onChange,
}: NotificationTriggerToggleProps): JSX.Element {
  const intl = useIntl()
  const keyFragment = TRIGGER_KEY[trigger]
  const groupLabel = intl.formatMessage({
    id: `settings.notifications.${keyFragment}.label`,
  })
  return (
    <fieldset
      data-component="notification-trigger-toggle"
      data-trigger={trigger}
      data-selected={selected}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        width: '100%',
        border: 'none',
        margin: 0,
        padding: 0,
      }}
    >
      <legend
        style={{
          fontFamily: 'Fredoka, sans-serif',
          fontWeight: 700,
          fontSize: 'var(--font-size-14)',
          color: 'var(--color-text-primary)',
          padding: 0,
          marginBottom: 'var(--space-1, 4px)',
        }}
      >
        <FormattedMessage id={`settings.notifications.${keyFragment}.label`} />
      </legend>
      <p
        style={{
          margin: 0,
          fontFamily: 'Manrope, system-ui, sans-serif',
          fontWeight: 500,
          fontSize: 'var(--font-size-14)',
          color: 'var(--color-text-secondary)',
          lineHeight: 1.5,
        }}
      >
        <FormattedMessage id={`settings.notifications.${keyFragment}.desc`} />
      </p>
      <Seg<OnOff>
        ariaLabel={groupLabel}
        value={selected ? 'on' : 'off'}
        onChange={(next) => {
          const v = next === 'on'
          if (v !== selected) onChange(v)
        }}
        options={[
          {
            value: 'on',
            label: <FormattedMessage id="settings.notifications.option.on" />,
          },
          {
            value: 'off',
            label: <FormattedMessage id="settings.notifications.option.off" />,
          },
        ]}
      />
    </fieldset>
  )
}
